/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import { useTranslation } from "react-i18next";

import { calculateSignupDate } from "./utils";
import { FacilityManagementFilterConditionData } from "../../../models/FacilityFilterConditionData";
import { InternalLabelData } from "../../../models/InternalLabelData";
import { LocationData } from "../../../models/LocationData";
import { OrganizationData } from "../../../models/OrganizationData";
import { getFirstCategory } from "../../../utils/getMainCategory";
import { FacilityIssuesIcon } from "../../Common/FacilityIssuesIcon";
import { Pagination } from "../../Pagination";
import ScoreItem from "../../ScoreItem";
import ListTopBar from "../ListTopBar";
import ModalAssignInternalLabels from "../ModalAssignInternalLabels";

import "./styles.scss";

export interface IFacilityManagementListProps {
  dataList: LocationData[];
  totalCount: number;
  conditionFormData: FacilityManagementFilterConditionData;
  filterFormData: any;
  organizationId: string;
  onClickFilter: () => void;
  onClickClearFilterCondition: () => void;
  onChangeFilterFormData: (filterFormData: any) => void;
  onClickAssignOrg: (facilityItem: LocationData) => void;
  onUpdateAssignLabels: (
    locationId: number,
    addIds: string[],
    deleteIds: string[]
  ) => Promise<void>;
}

export const FacilityManagementList: React.FunctionComponent<
  IFacilityManagementListProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityManagementPage.facilityManagementList.${key}`);

  const {
    dataList,
    totalCount,
    conditionFormData,
    filterFormData, // organizationId,
    onClickFilter,
    onClickClearFilterCondition,
    onChangeFilterFormData,
    onClickAssignOrg,
    onUpdateAssignLabels,
  } = props;

  const [showAssignLabelsModal, setShowAssignLabelsModal] =
    React.useState(false);
  const [currentLocation, setCurrentLocation] =
    React.useState<LocationData | null>(null);

  const renderLocationAssignedStatus = (item: any) => {
    if (item.organizations.length > 0) {
      return (
        <ul className="txt">
          {item.organizations.map(
            (organizationItem: OrganizationData, organizationIndex: number) => (
              <li key={organizationIndex}>{organizationItem.name}</li>
            )
          )}
        </ul>
      );
    }

    if (item.independent_use) {
      return <div className="txt">{t("independent_use")}</div>;
    }

    return <div className="txt red-txt">{t("unassigned")}</div>;
  };

  const handleOpenAssignLabels = (item: LocationData) => {
    setCurrentLocation(item);
    setShowAssignLabelsModal(true);
  };

  const handleCloseAssignLabels = () => {
    setShowAssignLabelsModal(false);
    setCurrentLocation(null);
  };

  const handleUpdateAssignLabels = async (
    addIds: string[],
    deleteIds: string[]
  ) => {
    if (!currentLocation) return;
    const locationId = currentLocation.id;
    await onUpdateAssignLabels(locationId, addIds, deleteIds);
    setShowAssignLabelsModal(false);
    setCurrentLocation(null);
  };

  return (
    <>
      <div className="white-border-panel">
        <ListTopBar
          conditionFormData={conditionFormData}
          filterFormData={filterFormData}
          onClickFilter={() => {
            onClickFilter();
          }}
          onClickClearFilterCondition={() => {
            onClickClearFilterCondition();
          }}
          onChangeFilterFormData={(filterFormData: any) => {
            onChangeFilterFormData(filterFormData);
          }}
        />

        <div className="table-data table-data-grid table-data-grid table-data-grid-pc-only facility-management-list">
          <div className="row-th">
            <div className="col-th">
              <div className="spacing">{t("facility_name")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("organization_name")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("loc_admin_present")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("date_signup_completed")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("location")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("category")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("is_archived")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("health_index")}</div>
            </div>
            <div className="col-th center">
              <div className="spacing">{t("internal_labels")}</div>
            </div>
          </div>
          {dataList
            .slice(0, filterFormData.numberPerPage)
            .map((item: LocationData, indexFacility: number) => (
              <div className="row-td" key={indexFacility}>
                <div className="col-td">
                  <div className="spacing">
                    <a
                      href={`/location/${item.id}`}
                      className="blue-txt facility-name-link"
                    >
                      {item.name}
                      <FacilityIssuesIcon facility={item} />
                    </a>
                    {/*
                    <NavLink to={`/location${organizationId ? `/${organizationId}` : ''}/${item.id}`} className='blue-link'>
                      {item.name}
                    </NavLink>
                    */}
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    {renderLocationAssignedStatus(item)}
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    {item?.has_loc_admins ? "True" : "False"}
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    {calculateSignupDate(item).format("YYYY-MM-DD")}
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    <div className="txt">
                      {item.prefecture} {item.city}
                    </div>
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">{getFirstCategory(item)}</div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    {item.is_archived ? "True" : "False"}
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    <ScoreItem score={item.stat.score} />
                  </div>
                </div>
                <div className="col-td center">
                  <div className="spacing">
                    {item.internal_labels && item.internal_labels.length > 0 ? (
                      <span className="label-chip">
                        {item.internal_labels
                          .map((label: InternalLabelData) => label.name)
                          .join(", ")}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div className="col-td center buttons-section">
                  <div className="spacing">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        onClickAssignOrg(item);
                      }}
                    >
                      {t("assign_org")}
                    </button>
                    <a className="btn btn-blue" href={`/location/${item.id}`}>
                      {t("see_screen")}
                    </a>
                    <button
                      className="btn btn-blue"
                      onClick={() => handleOpenAssignLabels(item)}
                    >
                      {t("assign_internal_labels")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      <Pagination
        filterFormData={filterFormData}
        totalRowsNumber={totalCount}
        onChangeFilterFormData={(filterFormData: any) => {
          onChangeFilterFormData(filterFormData);
        }}
      />

      {showAssignLabelsModal && currentLocation && (
        <ModalAssignInternalLabels
          locationData={currentLocation}
          onClose={handleCloseAssignLabels}
          onUpdateAssignLabels={handleUpdateAssignLabels}
        />
      )}
    </>
  );
};

export default FacilityManagementList;
